import React from 'react';
import { Badge } from '../ui/badge';
import { ArrowRight } from 'lucide-react';

/**
 * RebalanceNodeDiff — CAST-AI-style "preview planned changes" node diff.
 *
 * Renders a two-column remove → create table for a single RebalancePlan when
 * the agent (1.0.25+) reports per-node descriptors:
 *   nodes_to_remove_detail: [{node_name, instance_type, capacity_type,
 *                             status, reason}]   (all but node_name omitempty)
 *   nodes_to_create:        [{instance_type, capacity_type, status}]
 *
 * Shared by RebalancePreviewPanel (latest-plan preview) and
 * InClusterOptimizerPanel (per-plan expand). Purely presentational — the
 * callers own fetching and the decision to fall back to count-only display
 * when the arrays don't carry objects (older agents send int counts).
 * Every field is optional; missing values render as em-dash, never crash.
 */

// A descriptor list "carries objects" only when at least one entry is a
// plain object — older agents send int counts / plain strings instead.
export const hasNodeObjects = (list) =>
  Array.isArray(list) && list.some((n) => n != null && typeof n === 'object');

// spot/on-demand chip. Unknown/absent capacity type renders nothing rather
// than an empty badge (the agent sends "" when the Node is already gone).
const capacityBadge = (capacityType) => {
  if (!capacityType || typeof capacityType !== 'string') return null;
  const ct = capacityType.toLowerCase();
  if (ct === 'spot') {
    return <Badge className="bg-amber-100 text-amber-800 text-[10px]">spot</Badge>;
  }
  if (ct === 'on-demand' || ct === 'on_demand') {
    return <Badge className="bg-blue-100 text-blue-800 text-[10px]">on-demand</Badge>;
  }
  return <Badge className="bg-gray-100 text-gray-700 text-[10px]">{capacityType}</Badge>;
};

// Per-node deletion/creation status chip — mirrors the phase-badge vocabulary
// used across the page (green done, red failed, blue in-flight, gray other).
const statusChip = (status, reason) => {
  if (!status || typeof status !== 'string') return null;
  const title = reason || undefined;
  switch (status) {
    case 'Success':
    case 'Completed':
      return <Badge className="bg-green-100 text-green-800 text-[10px]" title={title}>{status}</Badge>;
    case 'Failed':
    case 'Error':
      return <Badge className="bg-red-100 text-red-800 text-[10px]" title={title}>{status}</Badge>;
    case 'InProgress':
    case 'Pending':
      return <Badge className="bg-blue-100 text-blue-800 text-[10px]" title={title}>{status}</Badge>;
    default:
      return <Badge className="bg-gray-100 text-gray-700 text-[10px]" title={title}>{status}</Badge>;
  }
};

// Cap rendered rows so a pathological plan (agent caps at 50/side anyway)
// never blows up the panel; the remainder is summarised as "+N more".
const MAX_ROWS = 12;

// moChip renders the engine-priced per-node $/mo (agent 1.0.26+ sends
// monthly_cost_usd from the plan spec). Absent/zero renders nothing — older
// agents simply show no money column, never a fake $0.
const moChip = (usd, tone) => {
  if (!usd || typeof usd !== 'number') return null;
  const t = tone === 'add'
    ? 'text-emerald-700 dark:text-emerald-400'
    : 'text-slate-500 dark:text-slate-400';
  return (
    <span className={`ml-auto tabular-nums font-medium ${t}`}>
      ${Math.round(usd).toLocaleString()}/mo
    </span>
  );
};

// classChip renders the engine's green/yellow/red per-node verdict (from the
// optops-node-classification report); absent classification renders nothing.
const classChip = (cls) => {
  if (!cls?.class) return null;
  const tone = cls.class === 'green'
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : cls.class === 'yellow'
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
      : 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300';
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${tone}`} title={cls.reason || ''}>
      {cls.class}
    </span>
  );
};

const RebalanceNodeDiff = ({ removeNodes, createNodes, classification }) => {
  const removes = Array.isArray(removeNodes)
    ? removeNodes.filter((n) => n != null && typeof n === 'object')
    : [];
  const creates = Array.isArray(createNodes)
    ? createNodes.filter((n) => n != null && typeof n === 'object')
    : [];
  if (removes.length === 0 && creates.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Remove side — node name + instance shape + capacity + per-node status */}
      <div>
        <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
          Remove ({removes.length})
        </p>
        {removes.length === 0 ? (
          <p className="text-xs text-gray-400">none</p>
        ) : (
          <ul className="text-xs text-gray-700 space-y-1">
            {removes.slice(0, MAX_ROWS).map((n, i) => (
              <li key={n.node_name || i} className="flex items-center gap-2 flex-wrap">
                <span className="font-mono truncate max-w-[14rem]" title={n.node_name || ''}>
                  {n.node_name || '—'}
                </span>
                <span className="text-gray-500">{n.instance_type || '—'}</span>
                {capacityBadge(n.capacity_type)}
                {statusChip(n.status, n.reason)}
                {classChip(classification?.[n.node_name])}
                {moChip(n.monthly_cost_usd, 'remove')}
              </li>
            ))}
            {removes.length > MAX_ROWS && (
              <li className="text-gray-400">+{removes.length - MAX_ROWS} more</li>
            )}
          </ul>
        )}
      </div>
      {/* Create side — instance shape + capacity + creation status. The engine
          CRD carries no node names for creates (Karpenter picks them). */}
      <div>
        <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
          <ArrowRight className="h-3 w-3 text-gray-400" />
          Create ({creates.length})
        </p>
        {creates.length === 0 ? (
          <p className="text-xs text-gray-400">none</p>
        ) : (
          <ul className="text-xs text-gray-700 space-y-1">
            {creates.slice(0, MAX_ROWS).map((n, i) => (
              <li key={i} className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{n.instance_type || '—'}</span>
                {capacityBadge(n.capacity_type)}
                {statusChip(n.status, n.reason)}
                {classChip(classification?.[n.node_name])}
                {moChip(n.monthly_cost_usd, 'add')}
              </li>
            ))}
            {creates.length > MAX_ROWS && (
              <li className="text-gray-400">+{creates.length - MAX_ROWS} more</li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default RebalanceNodeDiff;
